import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ContractAnalyzer } from "@/components/ContractAnalyzer";
import {
  Lock,
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Lightbulb,
  MessageCircleQuestion,
  ListChecks,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { LucideIcon } from "lucide-react";
import { useSubscription } from "@/hooks/use-subscription";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

type RelatedClause = { name: string; slug: string };
type RelatedTool = { name: string; slug: string };
type FAQ = { q: string; a: string };

type ToolConfig = {
  slug: string;
  name: string;
  h1: string; // SEO H1
  metaTitle: string; // ≤60
  metaDescription: string; // ≤160
  tagline: string;
  description: string;
  bullets: string[];
  // SEO content
  longExplanation: string[]; // paragraphs (300–800 words combined)
  sampleInput: string; // example clause text
  sampleOutput: { verdict: string; keyTerms: string[]; redFlags: string[] };
  outputExplanation: string;
  faq: FAQ[];
  relatedClauses: RelatedClause[];
  relatedTools: RelatedTool[];
  locked?: boolean;
};

export function ToolPage({ tool }: { tool: ToolConfig }) {
  const { isPro } = useSubscription();
  const unlocked = !tool.locked || isPro;
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="relative overflow-hidden border-b border-border">
          <div className="absolute inset-0 bg-gradient-glow" aria-hidden />
          <div className="relative mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <Link
              to="/"
              className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> All tools
            </Link>
            <div className="max-w-2xl">
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">{tool.h1}</h1>
              <p className="mt-3 text-lg text-muted-foreground">{tool.tagline}</p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                {tool.description}
              </p>
              <ul className="mt-6 grid gap-2 sm:grid-cols-2">
                {tool.bullets.map((b) => (
                  <li key={b} className="flex gap-2 text-sm">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-glow" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="py-12 sm:py-16">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            {unlocked ? (
              <ContractAnalyzer toolSlug={tool.slug} />
            ) : (
              <LockedState name={tool.name} />
            )}
            {unlocked && !isPro && (
              <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-primary-glow/30 bg-gradient-to-r from-primary/5 to-primary-glow/5 p-5 shadow-card">
                <div>
                  <p className="text-sm font-bold">Sign with confidence — go Pro</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Unlimited analyses · clause-by-clause explanations · risk score · PDF export ·
                    history · side-by-side comparison.
                  </p>
                </div>
                <Button variant="hero" size="sm" asChild className="gap-2">
                  <Link to="/pricing">
                    Start 7-day trial
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Long-form SEO content (only for unlocked free tools — locked Pro pages keep the upsell focused) */}
        {unlocked && (
          <>
            <section className="border-t border-border bg-surface/30 py-14">
              <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                <h2 className="flex items-center gap-2 text-2xl font-bold">
                  <ListChecks className="h-6 w-6 text-primary-glow" />
                  How {tool.name} works
                </h2>
                <div className="mt-4 space-y-4 text-base leading-relaxed text-foreground/90">
                  {tool.longExplanation.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </div>
            </section>

            <section className="py-14">
              <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                <h2 className="flex items-center gap-2 text-2xl font-bold">
                  <FileText className="h-6 w-6 text-primary-glow" />
                  Sample input
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Paste a clause that looks like this — legaldecoder reads the whole document, but
                  here&apos;s a typical chunk users analyze.
                </p>
                <pre className="mt-4 whitespace-pre-wrap rounded-xl border border-border bg-muted/30 p-4 font-mono text-sm leading-relaxed">
                  {tool.sampleInput}
                </pre>

                <h2 className="mt-10 flex items-center gap-2 text-2xl font-bold">
                  <Lightbulb className="h-6 w-6 text-primary-glow" />
                  Sample output
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">{tool.outputExplanation}</p>

                <div className="mt-5 rounded-2xl border border-border bg-card p-5 shadow-card">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Verdict
                    </span>
                    <span className="rounded-full bg-warning/15 px-3 py-1 text-xs font-bold text-warning">
                      {tool.sampleOutput.verdict}
                    </span>
                  </div>
                  <div className="mt-5 grid gap-5 sm:grid-cols-2">
                    <div>
                      <h3 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-foreground/80">
                        <CheckCircle2 className="h-4 w-4" /> Key terms
                      </h3>
                      <ul className="space-y-1.5 text-sm">
                        {tool.sampleOutput.keyTerms.map((k) => (
                          <li key={k} className="flex gap-2">
                            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-glow" />
                            {k}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-destructive">
                        <AlertTriangle className="h-4 w-4" /> Red flags
                      </h3>
                      <ul className="space-y-1.5 text-sm">
                        {tool.sampleOutput.redFlags.map((r) => (
                          <li key={r} className="flex gap-2">
                            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive" />
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="border-t border-border bg-surface/30 py-14">
              <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                <h2 className="flex items-center gap-2 text-2xl font-bold">
                  <MessageCircleQuestion className="h-6 w-6 text-primary-glow" />
                  Frequently asked questions
                </h2>
                <Accordion type="single" collapsible className="mt-4 w-full">
                  {tool.faq.map((f, i) => (
                    <AccordionItem key={f.q} value={`item-${i}`}>
                      <AccordionTrigger>{f.q}</AccordionTrigger>
                      <AccordionContent>{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </section>

            <section className="py-14">
              <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                <h2 className="text-2xl font-bold">Related clause explainers</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Free plain-English breakdowns of the clauses {tool.name} looks for.
                </p>
                <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                  {tool.relatedClauses.map((c) => (
                    <li key={c.slug}>
                      <Link
                        to="/clauses/$slug"
                        params={{ slug: c.slug }}
                        className="group flex items-center justify-between rounded-xl border border-border bg-card p-4 shadow-card hover:border-primary-glow/40"
                      >
                        <span className="text-sm font-semibold group-hover:text-primary-glow">
                          {c.name}
                        </span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary-glow" />
                      </Link>
                    </li>
                  ))}
                </ul>

                {tool.relatedTools.length > 0 && (
                  <>
                    <h3 className="mt-10 text-xl font-bold">Related tools</h3>
                    <ul className="mt-3 grid gap-3 sm:grid-cols-2">
                      {tool.relatedTools.map((t) => (
                        <li key={t.slug}>
                          <Link
                            to="/tools/$slug"
                            params={{ slug: t.slug }}
                            className="group flex items-center justify-between rounded-xl border border-border bg-card p-4 shadow-card hover:border-primary-glow/40"
                          >
                            <span className="text-sm font-semibold group-hover:text-primary-glow">
                              {t.name}
                            </span>
                            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary-glow" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                <p className="mt-12 text-center text-xs uppercase tracking-wider text-muted-foreground">
                  Not legal advice. For informational purposes only.
                </p>
              </div>
            </section>
          </>
        )}

        <Footer />
      </main>
    </div>
  );
}

function LockedState({ name }: { name: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-10 text-center shadow-card">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary-glow shadow-elegant">
        <Lock className="h-7 w-7 text-primary-foreground" />
      </div>
      <h2 className="text-2xl font-bold">{name} is a Pro feature</h2>
      <p className="mx-auto mt-3 max-w-md text-muted-foreground">
        Sign in or upgrade to Pro to unlock this advanced contract tool.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Button variant="hero" size="lg" asChild>
          <Link to="/pricing">View Pricing</Link>
        </Button>
        <Button variant="outline" size="lg" asChild>
          <Link to="/">Try Free Tools</Link>
        </Button>
      </div>
    </div>
  );
}

export const TOOLS: Record<string, ToolConfig> = {
  "lease-decoder": {
    slug: "lease-decoder",
    name: "Lease Decoder",
    h1: "Lease Decoder — Understand Your Rental Agreement Before You Sign",
    metaTitle: "Lease Decoder — Free AI Rental Lease Review",
    metaDescription:
      "Free AI lease analyzer. Spot illegal deposits, auto-renewal traps, and unfair entry clauses in your apartment lease in seconds.",
    tagline: "Know your rights before you move in.",
    description:
      "Apartment leases hide fees, predatory renewal clauses, and tenant-rights violations in dense legalese. Lease Decoder reads every line so you don't have to.",
    bullets: [
      "Catches illegal entry-notice clauses",
      "Flags excessive deposits & late fees",
      "Compares to your state's tenant laws",
      "Highlights auto-renewal traps",
    ],
    longExplanation: [
      "A residential lease is the largest financial document most people sign in any given year — often committing them to $20,000 or more in rent and fees over a single 12-month term. Yet the average tenant spends less than 10 minutes reading the document before signing. Lease Decoder closes that gap by reading every clause in your rental agreement and translating the dense legalese into plain English you can act on.",
      "When you upload a lease, Lease Decoder identifies the clauses that cost renters real money: the security deposit clause, the early termination clause, the auto-renewal clause, the late fees clause, and the landlord entry notice. It compares each one against fair, industry-standard language and flags anything that is unusually one-sided or, in some cases, outright illegal under state tenant law. The result is a side-by-side picture of what your landlord wants, what is reasonable, and where you have room to negotiate.",
      "Some of the most common traps Lease Decoder surfaces include security deposits above the legal cap (most states allow only 1–2 months' rent), 'sole discretion' deduction language that lets landlords keep your deposit for routine wear and tear, auto-renewal clauses that lock you into another full year if you forget to give 60 days' notice, and late fee clauses that compound daily — often above the legal limit set by your state.",
      "Lease Decoder is built for renters, not landlords. Every red flag comes with a specific suggestion: what to ask for, what to strike, and what to walk away from. Whether you're signing your first apartment lease or renewing a long-term rental, you'll walk into the conversation knowing exactly what you're agreeing to.",
      "Your document is processed with bank-grade encryption and purged immediately after analysis. We never store your lease, share it, or use it for AI training. This is part of our zero-data-retention policy — your contract is yours, full stop.",
    ],
    sampleInput:
      "Tenant shall deposit with Landlord the sum of $4,500 as security for performance of Tenant's obligations. Landlord may apply the deposit to any amounts owed, including but not limited to cleaning, repainting, and general restoration, in Landlord's sole discretion. Unless either party gives written notice at least sixty (60) days prior to the expiration of the term, this Lease shall automatically renew for an additional twelve (12) month term at a rental rate determined by Landlord.",
    sampleOutput: {
      verdict: "High Risk — 3 red flags",
      keyTerms: [
        "Deposit: $4,500 (3× monthly rent)",
        "Term: 12 months, auto-renews",
        "Notice required: 60 days prior to renewal",
      ],
      redFlags: [
        "Deposit likely exceeds state legal cap",
        "'Sole discretion' over deductions — no itemized list required",
        "Auto-renewal with landlord-set rate (no cap)",
      ],
    },
    outputExplanation:
      "Lease Decoder returns a single Verdict (Safe, Caution, or High Risk), a list of Key Terms (rent, deposit, notice periods), and a prioritized list of Red Flags with plain-English explanations and negotiation suggestions.",
    faq: [
      {
        q: "Is my lease data stored?",
        a: "No. Your document is encrypted in transit, analyzed in memory, and purged immediately. We never use your contract for AI training.",
      },
      {
        q: "Does Lease Decoder know my state's tenant laws?",
        a: "Yes — it cross-references common state-level rules (deposit caps, notice periods, late fee limits). For final advice on edge cases, consult a local attorney.",
      },
      {
        q: "Can I use Lease Decoder for commercial leases?",
        a: "It's optimized for residential leases. Commercial leases use different terms (CAM, NNN, etc.) and we recommend a real estate attorney for those.",
      },
      {
        q: "What if I've already signed?",
        a: "You can still scan the lease to know exactly what you agreed to and where you have leverage at renewal — or grounds to push back on disputed deductions.",
      },
    ],
    relatedClauses: [
      { name: "Security Deposit Clause", slug: "security-deposit-clause" },
      { name: "Early Termination Clause", slug: "early-termination-clause" },
      { name: "Auto-Renewal Clause", slug: "auto-renewal-clause" },
      { name: "Entry Notice Clause", slug: "entry-notice-clause" },
      { name: "Late Fees Clause", slug: "late-fees-clause" },
    ],
    relatedTools: [
      { name: "NDA Scanner", slug: "nda-scanner" },
      { name: "Freelancer Protector", slug: "freelancer-protector" },
    ],
  },
  "nda-scanner": {
    slug: "nda-scanner",
    name: "NDA Scanner",
    h1: "NDA Scanner — Decode Confidentiality Agreements in Plain English",
    metaTitle: "NDA Scanner — Free AI Non-Disclosure Agreement Review",
    metaDescription:
      "Free AI NDA analyzer. Spot overbroad confidentiality, unfair duration, and one-sided clauses before you sign.",
    tagline: "Understand what you can — and can't — say.",
    description:
      "NDAs can quietly extend years past your job, restrict your career, or expose you to massive damages. Scan before you sign.",
    bullets: [
      "Identifies overbroad scope",
      "Calculates real obligation duration",
      "Flags one-sided remedy clauses",
      "Shows what's industry-standard",
    ],
    longExplanation: [
      "Non-disclosure agreements (NDAs) are signed every day — for new jobs, contractor engagements, board meetings, fundraising conversations, even casual product demos. Most are reasonable. A meaningful percentage are not. NDA Scanner reads your confidentiality agreement clause by clause and tells you which is which, in language a non-lawyer can act on.",
      "A fair NDA defines 'Confidential Information' narrowly (specifically marked materials or clearly proprietary business data), lasts 2–5 years after the relationship ends, and includes standard exceptions for information that is already public, that you knew before the relationship, or that you developed independently. Anything broader than that — especially 'all information of any kind' or 'in perpetuity' — is a red flag worth pushing back on.",
      "NDA Scanner extracts the core mechanics of your agreement automatically: the definition of confidential information, the duration, whether obligations are mutual or one-sided, what remedies the disclosing party can seek (damages, injunctions, attorneys' fees), and whether the NDA tries to function as a back-door non-compete by restricting where you can work next. Each finding comes with a fairness rating and a specific suggestion for negotiation.",
      "The scanner also surfaces less obvious risks: clauses that conflict with state whistleblower protections, language that tries to silence harassment claims (now restricted under the federal Speak Out Act and many state laws), and IP-grabbing language buried inside a confidentiality section that quietly assigns your ideas to the other party.",
      "Use NDA Scanner before signing a job offer, accepting a contractor engagement, or sharing your startup idea with an investor. You'll know in seconds whether the agreement is industry-standard or unusually aggressive — and you'll have specific, written points to negotiate.",
    ],
    sampleInput:
      "Recipient agrees that all information of any kind disclosed by or learned about Discloser shall be considered Confidential Information and shall not be disclosed for any purpose, in perpetuity. Recipient further agrees not to engage with any competitor of Discloser for a period of 24 months following termination.",
    sampleOutput: {
      verdict: "High Risk — 3 red flags",
      keyTerms: [
        "Scope: all information of any kind",
        "Duration: perpetual (no end date)",
        "Embedded non-compete: 24 months",
      ],
      redFlags: [
        "'Any information' is overbroad — should be limited to marked confidential material",
        "Perpetual duration unenforceable in many states",
        "Hidden non-compete inside an NDA",
      ],
    },
    outputExplanation:
      "NDA Scanner returns a Verdict, the key obligations (scope, duration, mutual vs one-sided), and a list of red flags with plain-English explanations.",
    faq: [
      {
        q: "What's a normal NDA duration?",
        a: "2–5 years after the relationship ends is standard. True trade secrets can be perpetual, but only those — not 'all information.'",
      },
      {
        q: "Can an NDA stop me from reporting illegal behavior?",
        a: "No. Federal and most state laws protect whistleblower disclosures and (under the Speak Out Act) sexual harassment claims, regardless of NDA language.",
      },
      {
        q: "Should I sign a one-sided NDA?",
        a: "If you're also sharing sensitive info, push for mutual obligations. One-sided NDAs are common in employment but should be limited in scope and duration.",
      },
      {
        q: "Is my document stored?",
        a: "No. Your NDA is encrypted in transit, analyzed in memory, and purged immediately. Zero data retention.",
      },
    ],
    relatedClauses: [
      { name: "Confidentiality Clause", slug: "confidentiality-clause" },
      { name: "Non-Compete Clause", slug: "non-compete-clause" },
      { name: "IP Assignment Clause", slug: "ip-assignment-clause" },
    ],
    relatedTools: [
      { name: "Non-Compete Analyzer", slug: "non-compete-analyzer" },
      { name: "Freelancer Protector", slug: "freelancer-protector" },
    ],
  },
  "freelancer-protector": {
    slug: "freelancer-protector",
    name: "Freelancer Protector",
    h1: "Freelancer Protector — Catch Unfair Clauses in Client Contracts",
    metaTitle: "Freelancer Protector — Free AI Freelance Contract Review",
    metaDescription:
      "Free AI freelance contract review. Spot unpaid IP transfers, scope creep, and unfair termination clauses before you sign.",
    tagline: "Get paid. Keep your IP. Avoid scope creep.",
    description:
      "Freelance contracts can transfer your IP for free, lock you into endless revisions, or delay payment indefinitely. Catch it first.",
    bullets: [
      "Detects unpaid IP transfers",
      "Spots scope-creep language",
      "Verifies fair payment terms",
      "Flags unfair termination clauses",
    ],
    longExplanation: [
      "Freelance and contractor agreements look short and friendly. Most are not. A typical client contract contains five to ten clauses that can quietly cost you money, your intellectual property, or both. Freelancer Protector reads your agreement and surfaces the specific clauses every independent worker should review before signing.",
      "The most expensive mistake freelancers make is signing an IP assignment clause without negotiating. Standard 'work for hire' language transfers everything you create — including code, designs, and writing — to the client the moment you deliver. If you don't get paid, you may still have signed away ownership. Freelancer Protector flags IP clauses that lack a 'payment first, transfer after' structure and suggests specific replacement language.",
      "Equally common is the open-ended scope clause: 'as reasonably requested by Client' or 'including any related deliverables.' This phrasing turns a defined project into unlimited work. The scanner detects scope-creep language and recommends a fixed deliverables list with explicit change-order pricing for anything outside it.",
      "Payment terms get analyzed automatically: net-30 vs net-60 vs net-90, kill fees, late payment interest, dispute resolution, and what happens if the client cancels mid-project. Termination for convenience clauses (which let the client walk away anytime) are flagged and matched against fair industry standards — typically 14–30 days written notice and payment for all work in progress.",
      "Indemnification clauses are often the most dangerous part of a freelance contract and the least understood. Freelancer Protector identifies one-sided indemnity language that would make you personally liable for the client's losses, suggests mutual indemnification, and recommends a liability cap tied to the fees paid under the agreement.",
    ],
    sampleInput:
      "Contractor hereby assigns all intellectual property created during the engagement to Client. Contractor shall complete the project and any related deliverables as reasonably requested by Client. Client may terminate this Agreement at any time, for any reason, with no obligation to pay for work not yet delivered. Contractor shall indemnify Client from any and all claims arising from or related to the Services.",
    sampleOutput: {
      verdict: "High Risk — 4 red flags",
      keyTerms: [
        "IP: full assignment, no payment trigger",
        "Scope: 'related deliverables as reasonably requested'",
        "Termination: anytime, no payment for in-progress work",
        "Indemnity: one-sided, uncapped",
      ],
      redFlags: [
        "IP transfers immediately, regardless of payment",
        "Open-ended scope invites scope creep",
        "Same-day termination with no kill fee",
        "Uncapped indemnification of client",
      ],
    },
    outputExplanation:
      "Freelancer Protector returns a Verdict and breaks the contract into Key Terms (scope, payment, IP, termination) and Red Flags, with negotiation suggestions for each.",
    faq: [
      {
        q: "What's a 'kill fee'?",
        a: "A pre-agreed payment owed if the client cancels mid-project. Common in creative work — typically 25–50% of the remaining contract value.",
      },
      {
        q: "Should I always have mutual indemnification?",
        a: "Yes — and ideally with a cap on liability tied to fees paid. One-sided uncapped indemnity is a major financial risk.",
      },
      {
        q: "Can I keep ownership of my work?",
        a: "Often, you can negotiate a license-to-use instead of full IP assignment, or tie IP transfer to final payment received.",
      },
      {
        q: "Is my contract data stored?",
        a: "No. We use zero data retention — your contract is purged immediately after analysis.",
      },
    ],
    relatedClauses: [
      { name: "IP Assignment Clause", slug: "ip-assignment-clause" },
      { name: "Indemnification Clause", slug: "indemnification-clause" },
      { name: "Termination for Convenience Clause", slug: "termination-for-convenience-clause" },
      { name: "Confidentiality Clause", slug: "confidentiality-clause" },
    ],
    relatedTools: [
      { name: "NDA Scanner", slug: "nda-scanner" },
      { name: "Non-Compete Analyzer", slug: "non-compete-analyzer" },
    ],
  },
  "non-compete-analyzer": {
    slug: "non-compete-analyzer",
    name: "Non-Compete Analyzer",
    h1: "Non-Compete Analyzer — Find Out If Your Clause Is Even Enforceable",
    metaTitle: "Non-Compete Analyzer — Free AI Non-Compete Review",
    metaDescription:
      "Free AI non-compete clause review. Check enforceability by state, duration, geography, and find specific points to negotiate.",
    tagline: "How much of your career are you signing away?",
    description:
      "Non-competes vary wildly by state and many are unenforceable. Find out what's actually binding before you commit.",
    bullets: [
      "State enforceability check",
      "Geographic & time-scope analysis",
      "Industry-specific risk scoring",
      "Suggests negotiation points",
    ],
    longExplanation: [
      "Non-compete clauses are some of the most consequential — and most misunderstood — language in any employment or contractor agreement. They can determine where you can work next, who you can work for, and for how long after you leave. Non-Compete Analyzer reads your specific clause and tells you, in plain English, what it actually does and whether your state will even enforce it.",
      "Enforceability is the first thing the analyzer checks. California, North Dakota, Oklahoma, and Minnesota largely refuse to enforce employee non-competes. Other states require restrictions to be 'reasonable' in duration (typically 6–12 months), geography (where you actually worked, not nationwide), and scope (specific competitors or roles, not 'any business that competes'). The federal landscape is also shifting, with the FTC rule on non-competes facing ongoing legal challenges.",
      "The analyzer extracts the three numbers that determine whether your non-compete will hold up in court: how long the restriction lasts, how broad the geography is, and how the prohibited activities are defined. If any of those is unusually broad — for example, a 24-month nationwide restriction on 'any competing business' — Non-Compete Analyzer flags it and suggests both a stronger negotiation position and the likely outcome if you challenge it.",
      "Non-Compete Analyzer also looks at related clauses that often hide non-compete-style restrictions in disguise: non-solicitation (can you contact former clients or coworkers?), garden-leave (does your employer have to pay you during the restricted period?), and 'no-hire' agreements between companies. These can be just as restrictive as a traditional non-compete and are sometimes more enforceable.",
      "Whether you're considering a new job, leaving your current one, or starting a competing business, this analyzer gives you the specific facts you need: what the clause actually says, what it actually means, and what your realistic options are. As always, edge cases warrant a quick check with an attorney in your jurisdiction.",
    ],
    sampleInput:
      "For a period of 24 months following termination of employment, Employee shall not directly or indirectly engage in any business that competes with Company anywhere in the United States. Employee shall further refrain from soliciting any current or former customer of Company for the same period.",
    sampleOutput: {
      verdict: "High Risk — likely overbroad",
      keyTerms: [
        "Duration: 24 months",
        "Geography: nationwide (US)",
        "Scope: 'any business that competes' (undefined)",
        "Includes non-solicitation",
      ],
      redFlags: [
        "Duration exceeds enforceable norm (6–12 months in most states)",
        "Nationwide scope likely unenforceable",
        "Vague 'any competing business' language",
        "No carve-out for termination without cause",
      ],
    },
    outputExplanation:
      "Non-Compete Analyzer returns a Verdict, the three key dimensions (duration, geography, scope), and red flags tied to your state's enforcement standards.",
    faq: [
      {
        q: "Are non-competes enforceable in my state?",
        a: "Depends entirely on the state. California, ND, OK, and MN largely don't enforce employee non-competes. Most others require 'reasonable' scope, time, and geography.",
      },
      {
        q: "What if I was laid off?",
        a: "Many courts won't enforce a non-compete if the employee was terminated without cause — but the clause must say so or you may need to litigate it. Negotiate this carve-out before signing.",
      },
      {
        q: "Does signing mean I can't ever compete?",
        a: "No. Non-competes are time-limited (typically 6–12 months when enforceable) and most have geographic and scope limits. Read the specific terms carefully.",
      },
      {
        q: "Is my contract stored?",
        a: "No. Zero data retention — your document is purged immediately after analysis.",
      },
    ],
    relatedClauses: [
      { name: "Non-Compete Clause", slug: "non-compete-clause" },
      { name: "Confidentiality Clause", slug: "confidentiality-clause" },
      { name: "IP Assignment Clause", slug: "ip-assignment-clause" },
    ],
    relatedTools: [
      { name: "NDA Scanner", slug: "nda-scanner" },
      { name: "Freelancer Protector", slug: "freelancer-protector" },
    ],
  },
  "employment-agreement-checker": {
    slug: "employment-agreement-checker",
    name: "Employment Agreement Checker",
    h1: "Employment Agreement Checker — Read Your Offer Like a Lawyer",
    metaTitle: "Employment Agreement Checker — Free AI Review",
    metaDescription:
      "Free AI employment agreement review. Decode at-will, non-compete, arbitration, severance, and IP clauses before you sign your offer.",
    tagline: "Don't sign your offer letter blind.",
    description:
      "Employment agreements bury career-defining clauses in dense legalese: non-competes, arbitration, IP assignments, and severance terms. We translate them — so you negotiate with the facts.",
    bullets: [
      "Decodes at-will, non-compete, non-solicit",
      "Flags forced arbitration & class waivers",
      "Reviews severance & change-in-control",
      "Calls out aggressive IP assignment",
    ],
    longExplanation: [
      "Your employment agreement decides far more than your salary. Hidden inside the offer letter are clauses that shape what you can do after the job ends, who owns the side projects you build at home, and whether you can ever sue your employer in court. Employment Agreement Checker reads every paragraph of your offer and explains the clauses that matter most — in plain English, with specific negotiation language you can use.",
      "The checker focuses on the four high-impact areas: restrictive covenants (non-compete, non-solicit, garden leave), forced arbitration with class-action waivers, IP assignment language that can swallow your personal projects, and termination provisions including severance and change-in-control vesting. Each finding is rated against industry-standard practice in your state and comes with a specific suggested edit.",
      "Restrictive covenants get the closest read. The analyzer flags non-competes longer than 12 months, geographic scope broader than where you actually work, and definitions of 'competing business' that are too vague to defend. It also separates non-competes from non-solicits — the latter are often more enforceable even in employee-friendly states like California.",
      "Arbitration clauses are scanned for the patterns that hurt employees most: class action waivers, employee-paid arbitration fees, and confidentiality language that prevents you from discussing the dispute. Recent federal law (the 2022 Speak Out Act) voids forced arbitration for sexual harassment and sexual assault claims — the checker confirms whether your contract acknowledges that carve-out.",
      "Finally, the analyzer surfaces what's missing. Many offer letters omit severance, change-in-control acceleration, or a guaranteed bonus. These are not gifts — they're standard negotiation points for senior roles, and we list them so you can ask before you sign.",
    ],
    sampleInput:
      "Employment is at-will. Employee agrees to a 24-month non-compete nationwide following termination. Employee assigns all inventions conceived during employment, regardless of resources used. All disputes shall be resolved by binding arbitration with class action waiver. Employee shall receive no severance.",
    sampleOutput: {
      verdict: "High Risk — 4 red flags",
      keyTerms: [
        "At-will employment",
        "Non-compete: 24 months, nationwide",
        "IP: all inventions during employment",
        "Forced arbitration + class waiver, no severance",
      ],
      redFlags: [
        "Non-compete duration likely unenforceable",
        "IP clause grabs personal projects (state law may protect you)",
        "Class action waiver",
        "Zero severance on without-cause termination",
      ],
    },
    outputExplanation:
      "Employment Agreement Checker returns a Verdict, the four key dimensions (restrictive covenants, IP, arbitration, severance), and red flags with negotiation language for each.",
    faq: [
      {
        q: "Can I really negotiate an offer letter?",
        a: "Yes — almost always for non-comp items (severance, vesting, restrictive covenants). The worst they can say is no, and most companies have room on these terms.",
      },
      {
        q: "Is the non-compete in my offer enforceable?",
        a: "Depends on your state. CA, ND, OK, and MN largely refuse to enforce employee non-competes. Other states require 'reasonable' scope, time, and geography.",
      },
      {
        q: "What does at-will employment really mean?",
        a: "Either side can end the relationship at any time, with or without notice — but not for an illegal reason like discrimination or retaliation.",
      },
      {
        q: "Is my offer data stored?",
        a: "No. Your document is encrypted in transit, analyzed in memory, and purged immediately. Zero data retention.",
      },
    ],
    relatedClauses: [
      { name: "Non-Compete Clause", slug: "non-compete-clause" },
      { name: "Non-Solicit Clause", slug: "non-solicit-clause" },
      { name: "Arbitration Clause", slug: "arbitration-clause" },
      { name: "Severance Clause", slug: "severance-clause" },
      { name: "At-Will Employment Clause", slug: "at-will-employment-clause" },
      { name: "IP Assignment Clause", slug: "ip-assignment-clause" },
    ],
    relatedTools: [
      { name: "Non-Compete Analyzer", slug: "non-compete-analyzer" },
      { name: "NDA Scanner", slug: "nda-scanner" },
    ],
  },
  "contractor-agreement-checker": {
    slug: "contractor-agreement-checker",
    name: "Contractor Agreement Checker",
    h1: "Contractor Agreement Checker — Spot Red Flags Before You Sign",
    metaTitle: "Contractor Agreement Checker — Free AI Review",
    metaDescription:
      "Free AI contractor agreement review. Decode payment terms, IP transfer, indemnification, and termination clauses in seconds.",
    tagline: "Get paid. Keep your IP. Limit your liability.",
    description:
      "Contractor and consulting agreements are where money, IP, and liability collide. We surface the clauses that quietly cost contractors thousands.",
    bullets: [
      "Verifies payment terms (net-30 vs net-90)",
      "Catches one-sided indemnification",
      "Flags IP transfer without payment trigger",
      "Reviews termination & kill fees",
    ],
    longExplanation: [
      "Contractor agreements look light — sometimes only a few pages — but they redistribute risk in ways most contractors don't notice until something goes wrong. Contractor Agreement Checker reads your specific agreement and surfaces the clauses where money flows the wrong way.",
      "Payment terms are the first focus. The analyzer extracts the invoice cycle (net-30 vs net-60 vs net-90), late payment interest, dispute resolution windows, and any 'pay-when-paid' language that ties your payment to the client's customer. It compares your terms against industry norms (net-15 to net-30 for most freelance work) and recommends specific changes if you're carrying too much float.",
      "Indemnification clauses are the largest hidden risk. A standard contractor contract often makes you indemnify the client for any claim 'arising from or related to' the services — without a cap and without mutual obligation. The checker rewrites this into a defensible position: mutual indemnity, capped at fees paid, with carve-outs only for your gross negligence or IP infringement.",
      "IP and usage rights get analyzed separately. Many agreements transfer IP at signing, before you've been paid in full. The analyzer recommends a 'payment first, transfer after' structure and flags overbroad usage rights (worldwide, perpetual, exclusive) that should command an uplift.",
      "Termination, kill fees, and notice are the final layer. The checker flags termination-for-convenience clauses that let the client cancel with no payment for in-progress work, and recommends a 14–30 day notice window plus a 25–50% kill fee on cancelled milestones.",
    ],
    sampleInput:
      "Contractor shall be paid net-90 from invoice date. Contractor assigns all IP upon execution. Contractor shall indemnify Client from any and all claims related to the Services. Client may terminate at any time without notice or payment for incomplete work.",
    sampleOutput: {
      verdict: "High Risk — 4 red flags",
      keyTerms: [
        "Payment: net-90",
        "IP: full transfer at signing",
        "Indemnity: one-sided, uncapped",
        "Termination: any time, no notice, no payment",
      ],
      redFlags: [
        "Net-90 is unusually long",
        "IP transferred before payment received",
        "Uncapped one-sided indemnity",
        "Client can cancel free with no kill fee",
      ],
    },
    outputExplanation:
      "Contractor Agreement Checker returns a Verdict, key terms (payment, IP, indemnity, termination) and red flags with specific negotiation language.",
    faq: [
      {
        q: "What's a fair payment term for contractor work?",
        a: "Net-15 to net-30 for most independent work. Net-60+ is unusual and should come with late-payment interest of 1–1.5% per month.",
      },
      {
        q: "Should I sign a one-sided indemnification?",
        a: "No — push for mutual indemnity, capped at fees paid, with carve-outs limited to your gross negligence and IP infringement.",
      },
      {
        q: "When should IP transfer?",
        a: "On final payment received, not at signing. Tie the IP transfer to payment — that's your leverage.",
      },
      {
        q: "Is my contract data stored?",
        a: "No. Zero data retention — your document is purged immediately after analysis.",
      },
    ],
    relatedClauses: [
      { name: "Payment Terms Clause", slug: "payment-terms-clause" },
      { name: "Kill Fee Clause", slug: "kill-fee-clause" },
      { name: "IP Assignment Clause", slug: "ip-assignment-clause" },
      { name: "Usage Rights Clause", slug: "usage-rights-clause" },
      { name: "Indemnification Clause", slug: "indemnification-clause" },
      { name: "Termination for Convenience Clause", slug: "termination-for-convenience-clause" },
      { name: "Limitation of Liability Clause", slug: "limitation-of-liability-clause" },
    ],
    relatedTools: [
      { name: "Freelancer Protector", slug: "freelancer-protector" },
      { name: "NDA Scanner", slug: "nda-scanner" },
    ],
  },
  "contract-comparison": {
    slug: "contract-comparison",
    name: "Contract Comparison",
    h1: "Contract Comparison — Your Version vs. Industry Standard",
    metaTitle: "Contract Comparison — Pro Side-by-Side Diff",
    metaDescription:
      "Compare your contract to a fair industry-standard version. See every change that hurts you, with negotiation suggestions.",
    tagline: "Your version vs. the standard. Side-by-side.",
    description:
      "See exactly how your contract differs from a fair, industry-standard version. Spot every change that hurts you.",
    bullets: [
      "Diff view of every clause",
      "Risk-scored deviations",
      "Suggested counter-language",
      "Export negotiation notes",
    ],
    longExplanation: [],
    sampleInput: "",
    sampleOutput: { verdict: "", keyTerms: [], redFlags: [] },
    outputExplanation: "",
    faq: [],
    relatedClauses: [],
    relatedTools: [],
    locked: true,
  },
  "clause-history": {
    slug: "clause-history",
    name: "Clause History Library",
    h1: "Clause History Library — Every Contract You've Signed, Searchable",
    metaTitle: "Clause History Library — Pro Vault for Your Contracts",
    metaDescription:
      "Build a private library of every contract you've signed. Search by clause, term, or counterparty — instantly.",
    tagline: "Every clause you've signed, searchable.",
    description:
      "Build a private library of every contract you've ever signed. Search by clause, term, or counterparty — instantly.",
    bullets: [
      "Encrypted private vault",
      "Full-text clause search",
      "Renewal & expiration alerts",
      "Cross-contract conflict detection",
    ],
    longExplanation: [],
    sampleInput: "",
    sampleOutput: { verdict: "", keyTerms: [], redFlags: [] },
    outputExplanation: "",
    faq: [],
    relatedClauses: [],
    relatedTools: [],
    locked: true,
  },
  "lawyer-referral": {
    slug: "lawyer-referral",
    name: "Lawyer Referral",
    h1: "Lawyer Referral — Vetted Attorneys When You Need One",
    metaTitle: "Lawyer Referral — Pro Attorney Matching",
    metaDescription:
      "Get matched with a licensed attorney in your jurisdiction for flat-fee consultations on high-risk contracts.",
    tagline: "Vetted attorneys, when you need one.",
    description:
      "When AI isn't enough, get matched with a licensed attorney in your jurisdiction — for a flat consultation fee.",
    bullets: [
      "Pre-vetted local attorneys",
      "Flat consultation pricing",
      "Same-day matching",
      "Direct integration with your analysis",
    ],
    longExplanation: [],
    sampleInput: "",
    sampleOutput: { verdict: "", keyTerms: [], redFlags: [] },
    outputExplanation: "",
    faq: [],
    relatedClauses: [],
    relatedTools: [],
    locked: true,
  },
};

export const Route = createFileRoute("/tools/$slug")({
  head: ({ params }) => {
    const tool = TOOLS[params.slug];
    const url = `https://legaldecoder.io/tools/${params.slug}`;
    return {
      meta: [
        { title: tool ? tool.metaTitle : "Tool — legaldecoder" },
        {
          name: "description",
          content: tool?.metaDescription ?? "Specialized contract analysis tool.",
        },
        { property: "og:title", content: tool ? tool.metaTitle : "legaldecoder Tool" },
        {
          property: "og:description",
          content: tool?.metaDescription ?? "Decode any contract instantly.",
        },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts:
        tool && tool.faq.length
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: tool.faq.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }),
              },
            ]
          : undefined,
    };
  },
  component: ToolRoute,
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-3xl font-bold">Tool not found</h1>
        <Link to="/" className="mt-4 inline-block text-primary-glow hover:underline">
          Back home
        </Link>
      </div>
    </div>
  ),
});

function ToolRoute() {
  const { slug } = Route.useParams();
  const tool = TOOLS[slug];
  if (!tool) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold">Tool not found</h1>
          <Link to="/" className="mt-4 inline-block text-primary-glow hover:underline">
            Back home
          </Link>
        </div>
      </div>
    );
  }
  return <ToolPage tool={tool} />;
}

// Suppress unused import warning
type _Unused = LucideIcon;
